import { HealthSystemDomainMortalityReasonsMortalityReasonViewModel } from '../api/HealthSystemApi';
import { PageableResult } from './common';
import { UserViewModel } from './user';
import { AnimalType } from './animal';
import { LocationViewModel, LocationStable } from './locations';
import { VeterinaryViewModel } from './vets';

export enum MedicalSupplyType {
  Any = 0,
  Vaccination = 10,
  Antibiotic = 20,
  Hormones = 30,
  Minerals = 40,
  Painkiller = 50,
  Other = 100
}

export enum MedicalSupplyMethod {
  Any = 0,
  Intermuscular = 10,
  Oral = 20
}

// Translation keys for the supply method, so the enum name is never shown to a user
// (or printed on a label) directly.
export const medicalSupplyMethodKeys: Record<MedicalSupplyMethod, string> = {
  [MedicalSupplyMethod.Any]: 'f-any',
  [MedicalSupplyMethod.Intermuscular]: 'f-supplies-method-muscular',
  [MedicalSupplyMethod.Oral]: 'f-supplies-method-oral'
};

export enum MedicalSupplyOrderStatus {
  Processing = 100,
  Confirmed = 200,
  Completed = 400
}

export enum NotificationRuleRegionType {
  Unknown = 0,
  Injection = 10,
  Mortality = 20
}

export interface InjectionHistoryResult {
  id: string;
  animalId: string;
  dateTime: string;
  weekNumber: number;
  dose: number;
  medicalSupply: MedicalSupplyViewModel;
  lotNumber: string;
  treatmentNumber: string;
  employee: UserViewModel;
  veterinary: VeterinaryViewModel;
  dayNumber: number;
  waitingTime: number;
}

export interface InjectionHistoryPageableQueryResult extends PageableResult<InjectionHistoryResult> {}

export interface MedicalSupplyViewModel {
  id: string;
  number: string;
  producer: string;
  method: MedicalSupplyMethod;
  supplyType: MedicalSupplyType;
  waitingTime: number;
  registrationNumber: string;
  lastUsedAt?: string | null;
}

export interface MedicalSupplyOderItem {
  bottle: MedicalSupplyBottleViewModel | null;
  bottleId: string;
  amount: number;
  deliveredAmount: number;
  delivered: boolean;
  symptoms: string;
  locations: LocationViewModel[];
  details: MedicalSupplyOrderItemDetails;
}

export interface InjectionHistoryStrategyViewModel {
  weekNumber: number;
  animalType: AnimalType;
  supplyInfo: SupplyInfo[];
}

export interface InjectionHistoryTrendDataViewModel {
  animalType: AnimalType;
  trendData: TrendDataInfo[];
}

export interface SupplyInfo {
  supply: MedicalSupplyViewModel;
  averageAge?: number;
  pregnancyDay?: number;
  lactatingDay?: number;
}

export interface TrendDataInfo {
  weekNumber: number;
  year?: number;
  animalCount: number;
  averageAge?: number | null;
  diedAnimalCount: number;
}

export interface AnimalAgeTrendDataInfo {
  ageInDays: number;
  ageInWeeks: number;
  supplyId: string;
  isDead: boolean;
}

export interface InjectionHistoryAgeTrendDataResponse {
  trendData: AnimalAgeTrendDataInfo[];
}

export interface FarmMedicalSupplyViewModel extends MedicalSupplyViewModel {
  id: string;
  farmId: string;
  medicalSupplyId: string;
  inStock: number;
  animalType: AnimalType;
}

export interface CreateMedicalSupplyOrderCommand {
  details: MedicalSupplyOrderDetails;
  items: MedicalSupplyOderItem[];
}

export interface MedicalSupplyOrderDetails {
  comments: string;

  successTreatment: boolean;
  animalsVaccinability: boolean;
  successVaccination: boolean;
}

export interface MedicalSupplyOrderConfirmation {
  dateTime: string;
  vetUserId: string;
  vetUserName: string;
  vetUserUda: string;
}

export interface MedicalSupplyOrderViewModel {
  id: string;
  number: string;
  farmId: string;
  userId: string;
  farmName: string;
  created: Date;
  completed: Date | null;
  status: MedicalSupplyOrderStatus;
  items: MedicalSupplyOrderItemViewModel[];
  details: MedicalSupplyOrderDetails;
  confirmation: MedicalSupplyOrderConfirmation;
}

export interface ConfirmMedicalSupplyOrderCommand {
  orderId: string;
  items: MedicalSupplyOrderItemViewModel[];
  successTreatment: boolean;
  animalsVaccinability: boolean;
  successVaccination: boolean;
}

export interface VeterinaryOrderViewModel extends MedicalSupplyOrderViewModel {}

export interface MedicalSupplyOrderItemViewModel {
  id: string;
  medicalSupplyOrderId: string;
  medicalSupplyBottle: MedicalSupplyBottleViewModel;
  amount: number;
  realAmount: number;
  delivered: boolean;
  reason: string;
  locations: LocationViewModel[] | null;
  details: MedicalSupplyOrderItemDetails;
}

export interface MedicalSupplyOrderItemDetails {
  symptoms: string;
  diagnosis: string;
  expire: Date | null;
  recurrentTreatment: boolean;
  animalAmount: number;
  averageWeight: number;
}

export interface MedicalSupplyOrdersQueryResult extends PageableResult<MedicalSupplyOrderViewModel> {}

export interface MedicalSupplyBottlesQueryResult extends PageableResult<MedicalSupplyBottleViewModel> {}

export interface MedicalSupplyBottleViewModel {
  id: string;
  medicalSupply: MedicalSupplyViewModel;
  lotNumber: string;
  treatmentNumber: string;
  registrationNumber: string;
  size: number;
  dosePerAnimal?: number;
  daysToUse: number;
  animalType: AnimalType;
  isHidden?: boolean;
  incrementStep: number;
  maxVolumeCC: number;
  minVolumeCC: number;
  ccPerDose?: number;
  kgPerOneCC?: number;
  startVolume: number;
  lastOrderDate?: string;
  lastUsedAt?: string | null;
}

export interface DiseaseMedicalSupply {
  medicalSupplyId: string;
  number: string;
  daysToTreat: number;
  dose: number;
}

export interface DiseaseViewModel {
  id: string;
  farmId: string;
  name: string;
  stableColours: DiseaseStableColour[];
  medicalSupply: DiseaseMedicalSupply;
}

/**
 * A mortality reason as offered by `MortalityReasonSelectModal`'s picker list and its
 * `mortalityReason`/`onChange` props. Trimmed to `id` and `name`, the only fields the modal reads;
 * `name` mirrors the API's nullable field.
 */
export interface MortalityReasonOption {
  id: string;
  name: string | null;
}

export const toMortalityReasonOption = (
  dto: HealthSystemDomainMortalityReasonsMortalityReasonViewModel
): MortalityReasonOption => ({
  id: dto.id,
  name: dto.name ?? null
});

export interface DiseaseStableColour {
  colour: string;
  stable: LocationStable;
}

export interface CreateDiseaseCommand {
  name: string;
  stableColours: DiseaseStableColour[];
  medicalSupplyId: string;
  daysToTreat: number;
  dose: number;
}

export interface FarmDiseasesQueryResult extends PageableResult<DiseaseViewModel> {}
